import React, { useState, useEffect, useRef } from "react";
import { Search, MapPin, Calendar, Filter } from "lucide-react";
import { SPONSORSHIP_COMPONENTS, EVENT_TYPES as RAW_EVENT_TYPES } from "@/utils/constants";
import SelectDropdown from "@/components/common/SelectDropdown";
import DatePicker from "@/components/common/DatePicker";
import CityAutocomplete from "@/components/common/CityAutocomplete";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "submitted", label: "Submitted" },
  { value: "sponsor_listed", label: "Sponsor Listed" },
  { value: "published", label: "Published" },
  { value: "archived", label: "Archived" },
];

const EVENT_TYPES = [{ value: "", label: "All Types" }, ...RAW_EVENT_TYPES];

const SORT_OPTIONS = [
  { value: "recent", label: "Recently Added" },
  { value: "oldest", label: "Oldest First" },
  { value: "start_date_asc", label: "Date: Upcoming" },
  { value: "start_date_desc", label: "Date: Past" },
  { value: "name_asc", label: "Name (A-Z)" },
  { value: "name_desc", label: "Name (Z-A)" },
];

const EventFilters = ({
  filters,
  onChange,
  componentCounts = [],
  extraFilters = null,
  showStatus = true,
  statusOptions = STATUS_OPTIONS
}) => {
  const [localSearch, setLocalSearch] = useState(filters.search || "");
  const [localCity, setLocalCity] = useState(filters.city || "");

  const handleCityChange = (city) => {
    setLocalCity(city);
    if (city.trim() === "" && filters.city !== "") {
      onChange("city", ""); // Clear filter if input is emptied
    }
  };

  const handleCityEnter = (city) => {
    onChange("city", city.trim());
  };

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (filters.search !== localSearch) {
        onChange("search", localSearch);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [localSearch, onChange, filters.search]);

  // City debounce logic removed; event search is now only triggered 
  // on selection (handleCitySelect) or on Enter key (handleCityKeyDown).

  // Sync external search/city clears
  useEffect(() => {
    setLocalSearch(filters.search || "");
  }, [filters.search]);

  useEffect(() => {
    setLocalCity(filters.city || "");
  }, [filters.city]);

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 space-y-4">
      {/* Top Row: Search and Core Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search events by title..."
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
          />
        </div>

        {showStatus && (
          <div className="w-full md:w-48">
             <SelectDropdown
                options={statusOptions}
                value={filters.status || ""}
                onChange={(e) => onChange("status", e.target.value)}
                placeholder="All Statuses"
             />
          </div>
        )}

        <div className="w-full md:w-48">
          <SelectDropdown
            options={EVENT_TYPES}
            value={filters.event_type || ""}
            onChange={(e) => onChange("event_type", e.target.value)}
            placeholder="All Event Types"
          />
        </div>

        <div className="w-full md:w-56">
          <select
            value={filters.component || ""}
            onChange={(e) => onChange("component", e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="">All Components</option>
            {SPONSORSHIP_COMPONENTS.map((comp) => {
              const countObj = componentCounts?.find(c => c.category === comp.value);
              const count = countObj ? countObj.count : 0;
              return (
                <option key={comp.value} value={comp.value}>
                  {comp.label} ({count})
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {/* Second Row: Additional Filters */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg border border-gray-100 dark:border-gray-600">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 font-medium whitespace-nowrap">
           <Filter className="w-4 h-4" /> Filters:
        </div>

        <div className="flex-1 min-w-[200px]">
          <CityAutocomplete
            value={localCity}
            onChange={handleCityChange}
            onEnter={handleCityEnter}
            placeholder="City..."
            inputClassName="border-gray-200 dark:border-gray-700"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <DatePicker
              value={filters.startDate || ""}
              onChange={(e) => onChange("startDate", e.target.value)}
              placeholder="From date"
              allowFuture={true}
              className="flex-1 md:flex-none md:w-40"
            />
            <span className="text-gray-500 dark:text-gray-400 text-sm">to</span>
            <DatePicker
              value={filters.endDate || ""}
              onChange={(e) => onChange("endDate", e.target.value)}
              placeholder="To date"
              allowFuture={true}
              className="flex-1 md:flex-none md:w-40"
            />
        </div>

        {/* Render Extra Filters specific to User Role (e.g. Organizer Username) */}
        {extraFilters}

        <div className="w-full md:w-48 md:ml-auto border-t md:border-t-0 md:border-l border-gray-200 dark:border-gray-600 pt-4 md:pt-0 md:pl-4 mt-2 md:mt-0">
          <SelectDropdown
            options={SORT_OPTIONS}
            value={filters.sort || "recent"}
            onChange={(e) => onChange("sort", e.target.value)}
            placeholder="Sort By..."
          />
        </div>
      </div>
    </div>
  );
};

export default EventFilters;
